import React, { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { DragDropProvider } from '@dnd-kit/react'
import { useSortable, isSortable } from '@dnd-kit/react/sortable'
import { ArrowsClockwise, CheckCircle, GitFork } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

import TextField from '../../../../../components/FormLegacy/FormFieldsV2/TextField'
import SelectField from '../../../../../components/FormLegacy/FormFieldsV2/SelectField'
import ToggleField from '../../../../../components/FormLegacy/FormFieldsV2/ToggleField'
import Icon from '../../../../../components/Icons'
import EditorText from '../../../../../components/EditorText/EditorText'
import {
  getDataInputTypes,
  getTaskInfoTypes,
  maxFormEntriesOptions,
  DATA_INPUT_ERROR_TYPES,
} from '../../helpers'
import api from '../../../../../api/api'
import RealPageFormModal from '../modals/RealPagePetFormModal'
import VehicleFormModal from '../modals/VehicleFormModal'

import ProviderItem from './ProviderItem'
import YardiSyncModal from './YardiSyncModal'

const MAX_LABEL_LENGTH = 30
const MAX_OPTIONS_COUNT = 10
const MAX_CTA_LENGTH = 20

const SortableProviderItem = ({ id, provider, index, onDelete, onEdit }) => {
  const { ref } = useSortable({ id, index })
  return (
    <div ref={ref} style={{ marginRight: 8 }}>
      <ProviderItem
        provider={provider}
        isEditable
        onDelete={onDelete}
        onEdit={onEdit}
        dragHandleProps={{}}
      />
    </div>
  )
}

const DataInput = ({
  onRemove = () => {},
  onCopy = () => {},
  onFieldChange = () => {},
  onAddOption = () => {},
  onRemoveOption = () => {},
  onChangeOption = () => {},
  onShowSetFrom = () => {},
  onShowAddProvider = () => {},
  onRemoveProvider = () => {},
  onEditProvider = () => {},
  onProviderDragEnd = () => {},
  dataInputState = {},
  dataInputIndex = 0,
  excludeForm = false,
  selectedBuilding = {},
  yardiCustomRecords = {},
  setYardiCustomRecords = () => {},
  onYardiSyncFieldsChange = () => {},
  inputs = [],
  disabled = false,
}) => {
  const { t } = useTranslation(['checklists-create-task'])
  const [realPagePetFormOpen, setRealPagePetFormOpen] = useState(false)
  const [realPageVehicleFormOpen, setRealPageVehicleFormOpen] = useState(false)
  const [vehicleFormOpen, setVehicleFormOpen] = useState(false)
  const [bookingTypeOptions, setBookingTypeOptions] = useState([])

  useEffect(() => {
    if (dataInputState.inputType !== 'service' || !selectedBuilding?.id) return

    const fetchBookingTypes = async () => {
      const { data } = await api.getBookingTypesByBuildingId(selectedBuilding.id, true)
      setBookingTypeOptions(data.map((s) => ({ label: s.name, value: s.id })))
    }

    fetchBookingTypes()
  }, [dataInputState.inputType, selectedBuilding?.id])
  const handleFieldChange = ({ target: { name, value } }) => onFieldChange(name, value)

  const handleProviderDragEnd = (event) => {
    const { operation, canceled } = event
    if (canceled) return
    const { source } = operation
    if (!isSortable(source)) return
    const sourceIndex = source.initialIndex
    const targetIndex = source.index
    if (sourceIndex === targetIndex) return
    onProviderDragEnd({
      source: { index: sourceIndex },
      destination: { index: targetIndex },
    })
  }

  const [yardiSyncModalOpen, setYardiSyncModalOpen] = useState(false)

  const onChangeEditorText = (content, text) => {
    handleFieldChange({
      target: { name: 'textInfo', value: text.trim() ? content : undefined },
    })
  }

  const onChangeLabelText = ({ target: { name, value } }) => {
    if (value.length <= MAX_LABEL_LENGTH) {
      onFieldChange(name, value)
    }
  }

  const onYardiModalSave = ({ otherOccupantCustomRecord, residentCustomRecord, fields }) => {
    onYardiSyncFieldsChange({
      yardiOtherOccupantCustomRecord: otherOccupantCustomRecord,
      yardiResidentCustomRecord: residentCustomRecord,
      fields,
    })
    setYardiSyncModalOpen(false)
  }

  const openYardiSyncModal = () => setYardiSyncModalOpen(true)
  const closeYardiSyncModal = () => setYardiSyncModalOpen(false)

  const renderBodyInputs = () => {
    switch (dataInputState.inputType) {
      case 'single_choice':
      case 'multiple_choice':
        const input =
          dataInputState.inputType === 'single_choice' ? (
            <input type="radio" disabled />
          ) : (
            <input type="checkbox" disabled />
          )

        const hasError =
          dataInputState.error &&
          dataInputState.error !== null &&
          (DATA_INPUT_ERROR_TYPES.EMPTY_OPTIONS === dataInputState.error.type ||
            DATA_INPUT_ERROR_TYPES.OPTION_EMPTY_LABEL === dataInputState.error.type)
        return (
          <div className="data-input-body__options">
            {dataInputState.options.map((option, index) => (
              <div key={`${+index}-option`} className="data-input-body__options__option">
                {input}
                <TextField
                  name="option-label-text"
                  onChange={(e) => onChangeOption(index, e.target.value)}
                  additionalClass={hasError && !option ? 'error' : ''}
                  value={option}
                />
                <button onClick={() => onRemoveOption(index)}>
                  <Icon icon="cross" />
                </button>
              </div>
            ))}
            <div className="data-input-body__options__option">
              {input}
              <button
                className={hasError ? 'error' : ''}
                disabled={dataInputState.options.length >= MAX_OPTIONS_COUNT}
                onClick={onAddOption}
              >
                {t('checklists-create-task:dataInput.addOptionButton')}
              </button>
            </div>
            {hasError ? <span className="error-label">{dataInputState.error.message}</span> : null}
          </div>
        )
      case 'form':
        const { form = [] } = dataInputState
        const alreadyHasInputs = form.length > 0

        return (
          <React.Fragment>
            <div className="data-input-body__inputs">
              <div>
                <div className="input-label">
                  {t('checklists-create-task:dataInput.formActionLabelLabel')}
                </div>
                <TextField
                  name="formActionLabel"
                  onChange={onChangeLabelText}
                  value={dataInputState.formActionLabel}
                  placeholder={t('checklists-create-task:dataInput.formActionLabelPlaceholder')}
                  isError={
                    dataInputState.error &&
                    dataInputState.error !== null &&
                    DATA_INPUT_ERROR_TYPES.NO_CALL_TO_ACTION === dataInputState.error.type
                  }
                  errorLabel={dataInputState.error && dataInputState.error.message}
                  disabled={disabled}
                />
                <span className="input-desc-label">
                  {t('checklists-create-task:dataInput.maxLengthInfo', { length: 20 })}
                </span>
              </div>
              <div>
                <div className="input-label">
                  {t('checklists-create-task:dataInput.maxFormEntriesLabel')}
                </div>
                <SelectField
                  name="formMaxEntries"
                  options={maxFormEntriesOptions}
                  placeholder=""
                  value={dataInputState.formMaxEntries}
                  onChange={handleFieldChange}
                  disabled={disabled}
                />
              </div>
            </div>
            <div className="data-form-controls">
              <button
                className="button button-black"
                disabled={disabled}
                onClick={() => onShowSetFrom(dataInputIndex)}
              >
                <GitFork size={20} />
                {alreadyHasInputs
                  ? t('checklists-create-task:dataInput.updateFormStructureButton')
                  : t('checklists-create-task:dataInput.setFormStructureButton')}
              </button>
              {alreadyHasInputs ? (
                <span>
                  {t('checklists-create-task:dataInput.formEntriesIncluded', {
                    count: form.length,
                  })}
                </span>
              ) : null}
              {dataInputState.error &&
              dataInputState.error !== null &&
              DATA_INPUT_ERROR_TYPES.EMPTY_FORM === dataInputState.error.type ? (
                <span className="error-label">{dataInputState.error.message}</span>
              ) : null}
            </div>
          </React.Fragment>
        )
      case 'text_info':
        return (
          <div className="data-input-body__form">
            <EditorText
              value={dataInputState.textInfo}
              onChange={onChangeEditorText}
              placeholder={t('checklists-create-task:customTaskForm.instructionsPlaceholder')}
            />
            {dataInputState.error &&
            dataInputState.error !== null &&
            DATA_INPUT_ERROR_TYPES.EMPTY_TEXT_INFO === dataInputState.error.type ? (
              <span className="error-label">{dataInputState.error.message}</span>
            ) : null}
          </div>
        )
      case 'providers_info':
        return (
          <React.Fragment>
            <div className="data-input-body__providers">
              {dataInputState.providersInfo.length > 0 ? (
                <DragDropProvider onDragEnd={handleProviderDragEnd}>
                  <div className="draggable-providers" style={{ width: '100%' }}>
                    {dataInputState.providersInfo.map((provider, index) => (
                      <SortableProviderItem
                        key={String(provider.id)}
                        id={String(provider.id)}
                        provider={provider}
                        index={index}
                        onDelete={() => onRemoveProvider(index)}
                        onEdit={() => onEditProvider(index)}
                      />
                    ))}
                  </div>
                </DragDropProvider>
              ) : null}
              <button
                className="button button-black"
                disabled={dataInputState.providersInfo.length >= 3}
                onClick={onShowAddProvider}
              >
                <Icon icon="plus" viewBox="0 -2 15 15" />
                {t('checklists-create-task:dataInput.addProviderButton')}
              </button>
            </div>
            {dataInputState.error &&
            dataInputState.error !== null &&
            DATA_INPUT_ERROR_TYPES.EMPTY_PROVIDERS === dataInputState.error.type ? (
              <span className="error-label">{dataInputState.error.message}</span>
            ) : null}
          </React.Fragment>
        )
      case 'realpage_pet':
        return (
          <div className="data-input-body__realpage-pet-form">
            <button className="button button-black" onClick={() => setRealPagePetFormOpen(true)}>
              <GitFork size={20} />
              {t('checklists-create-task:dataInput.formStructureButton')}
            </button>
            {realPagePetFormOpen && (
              <RealPageFormModal onClose={() => setRealPagePetFormOpen(false)} type="pet" />
            )}
          </div>
        )
      case 'realpage_vehicle':
        return (
          <div className="data-input-body__realpage-vehicle-form">
            <button
              className="button button-black"
              onClick={() => setRealPageVehicleFormOpen(true)}
            >
              <GitFork size={20} />
              {t('checklists-create-task:dataInput.formStructureButton')}
            </button>
            {realPageVehicleFormOpen && (
              <RealPageFormModal onClose={() => setRealPageVehicleFormOpen(false)} type="vehicle" />
            )}
          </div>
        )
      case 'service': {
        const onChangeServiceCta = ({ target: { name, value } }) => {
          if (value.length <= MAX_CTA_LENGTH) {
            onFieldChange(name, value)
          }
        }
        const serviceError = dataInputState.error && dataInputState.error !== null
        return (
          <div className="data-input-body__inputs">
            <div>
              <div className="input-label">
                {t('checklists-create-task:dataInput.serviceCtaLabel')}
              </div>
              <TextField
                name="formActionLabel"
                onChange={onChangeServiceCta}
                value={dataInputState.formActionLabel}
                placeholder={t('checklists-create-task:dataInput.serviceCtaPlaceholder')}
                isError={
                  serviceError &&
                  DATA_INPUT_ERROR_TYPES.NO_SERVICE_CTA === dataInputState.error.type
                }
                errorLabel={
                  serviceError &&
                  DATA_INPUT_ERROR_TYPES.NO_SERVICE_CTA === dataInputState.error.type
                    ? dataInputState.error.message
                    : ''
                }
              />
              <span className="input-desc-label">
                {t('checklists-create-task:dataInput.maxLengthInfo', { length: MAX_CTA_LENGTH })}
              </span>
            </div>
            <div>
              <div className="input-label">
                {t('checklists-create-task:dataInput.chooseServiceLabel')}
              </div>
              <SelectField
                name="bookingTypeId"
                options={bookingTypeOptions}
                placeholder={
                  bookingTypeOptions.length
                    ? t('checklists-create-task:dataInput.selectServicePlaceholder')
                    : t('checklists-create-task:dataInput.noServicesFoundPlaceholder')
                }
                value={dataInputState.bookingTypeId}
                onChange={handleFieldChange}
                isError={
                  serviceError && DATA_INPUT_ERROR_TYPES.NO_SERVICE === dataInputState.error.type
                }
              />
              {serviceError && DATA_INPUT_ERROR_TYPES.NO_SERVICE === dataInputState.error.type ? (
                <span className="error-label">{dataInputState.error.message}</span>
              ) : null}
            </div>
          </div>
        )
      }
      case 'vehicle_form':
        return (
          <div className="data-input-body__vehicle-form">
            <div>
              <div className="input-label">
                {t('checklists-create-task:dataInput.maxFormEntriesLabel')}
              </div>
              <SelectField
                name="formMaxEntries"
                options={maxFormEntriesOptions}
                placeholder=""
                value={dataInputState.formMaxEntries}
                onChange={handleFieldChange}
                disabled={disabled}
              />
            </div>
            <button className="button button-black " onClick={() => setVehicleFormOpen(true)}>
              <GitFork size={20} />
              {t('checklists-create-task:dataInput.formStructureButton')}
            </button>
            {vehicleFormOpen && <VehicleFormModal onClose={() => setVehicleFormOpen(false)} />}
          </div>
        )
      default:
        return null
    }
  }

  const isRealPageProperty = !!(
    selectedBuilding.realPageManagementId && selectedBuilding.realPagePropertyId
  )

  const dataInputTypes = useMemo(
    () => getDataInputTypes(isRealPageProperty, inputs, t),
    [isRealPageProperty, inputs, t],
  )

  const isDataInput = dataInputTypes.find((type) => type.value === dataInputState.inputType)

  // hide yardi sync button for 'form', 'text_info', and 'service' input types
  const hideYardiSync = ['form', 'text_info', 'service'].includes(dataInputState.inputType)

  const isYardiProperty = !!(selectedBuilding.yardiManagementId && selectedBuilding.yardiPropertyId)

  const yardiSyncedFieldNames = []
    .filter(Boolean)
    .map((field) => field.name)
    .join(' & ')

  // set synced fields to unsynced when input type is changed to 'form'
  useEffect(() => {
    if (hideYardiSync) {
      const yardiResidentCustomRecordId = dataInputState.yardiResidentCustomRecord
        ? dataInputState.yardiResidentCustomRecord.id
        : null
      const yardiOtherOccupantCustomRecordId = dataInputState.yardiOtherOccupantCustomRecord
        ? dataInputState.yardiOtherOccupantCustomRecord.id
        : null

      if (yardiResidentCustomRecordId || yardiOtherOccupantCustomRecordId) {
        onYardiSyncFieldsChange({
          yardiOtherOccupantCustomRecord: null,
          yardiResidentCustomRecord: null,
          fields: {
            // yardiCustomRecords is null until the records request resolves (or if it failed)
            residentCustomRecords: (yardiCustomRecords?.residentCustomRecords ?? []).map((field) =>
              field.id === yardiResidentCustomRecordId ? { ...field, synced: false } : field,
            ),
            otherOccupantCustomRecords: (yardiCustomRecords?.otherOccupantCustomRecords ?? []).map(
              (field) =>
                field.id === yardiOtherOccupantCustomRecordId ? { ...field, synced: false } : field,
            ),
          },
        })
      }
    }
  }, [hideYardiSync])

  return (
    <div
      className={`data-input ${
        dataInputState.error && dataInputState.error !== null ? 'error' : ''
      }`}
    >
      <div className="draggbale-head">
        <Icon icon="gripDots" />
      </div>
      <div className="data-input-container">
        <div className="data-input-body">
          {isDataInput ? (
            <div className="data-input-body__inputs">
              <div>
                <div className="input-label">
                  {t('checklists-create-task:dataInput.taskItemLabelLabel')}
                </div>
                <TextField
                  name="taskLabel"
                  onChange={onChangeLabelText}
                  value={dataInputState.taskLabel}
                  placeholder={t('checklists-create-task:dataInput.taskItemLabelPlaceholder')}
                  isError={
                    dataInputState.error &&
                    dataInputState.error !== null &&
                    DATA_INPUT_ERROR_TYPES.NO_LABEL === dataInputState.error.type
                  }
                  errorLabel={dataInputState.error && dataInputState.error.message}
                  disabled={disabled}
                />
              </div>
              <div>
                <div className="input-label">
                  {t('checklists-create-task:dataInput.selectInputTypeLabel')}
                </div>
                <SelectField
                  name="inputType"
                  options={dataInputTypes.filter((type) => !excludeForm || type.value !== 'form')}
                  placeholder=""
                  value={dataInputState.inputType}
                  onChange={handleFieldChange}
                  disabled={disabled}
                />
                {dataInputState.inputType === 'file_upload' && (
                  <span className="input-desc-label">
                    {t('checklists-create-task:dataInput.acceptedFileFormatsInfo')}
                  </span>
                )}
              </div>
            </div>
          ) : (
            <div className="data-input-body__inputs">
              <div>
                <div className="input-label">
                  {t('checklists-create-task:dataInput.selectInfoTypeLabel')}
                </div>
                <SelectField
                  name="inputType"
                  options={getTaskInfoTypes(t)}
                  placeholder=""
                  value={dataInputState.inputType}
                  onChange={handleFieldChange}
                  disabled={disabled}
                />
                {dataInputState.inputType === 'providers_info' && (
                  <span className="input-desc-label">
                    {t('checklists-create-task:dataInput.addUpTo3ProvidersInfo')}
                  </span>
                )}
              </div>
            </div>
          )}
          {renderBodyInputs()}
        </div>
        <div className="data-input-footer">
          <div>
            {isDataInput && dataInputState.inputType !== 'service' && (
              <ToggleField
                label={t('checklists-create-task:dataInput.requiredLabel')}
                name={`isRequired-${dataInputIndex}${excludeForm ? 'input' : 'form'}`}
                labelOnRight
                onChange={() => onFieldChange('isRequired', !dataInputState.isRequired)}
                value={
                  dataInputState.inputType === 'realpage_pet' ||
                  dataInputState.inputType === 'realpage_vehicle' ||
                  dataInputState.inputType === 'vehicle_form'
                    ? true
                    : dataInputState.isRequired
                }
                disabled={
                  dataInputState.inputType === 'realpage_pet' ||
                  dataInputState.inputType === 'realpage_vehicle' ||
                  dataInputState.inputType === 'vehicle_form' ||
                  disabled
                }
              />
            )}
          </div>
          <div>
            {['realpage_pet', 'realpage_vehicle'].includes(dataInputState.inputType) && (
              <div className="data-input-footer__sync-badge">
                <CheckCircle size={20} />
                {t('checklists-create-task:dataInput.syncingWithRealPage')}
              </div>
            )}
            {isDataInput && isYardiProperty && !hideYardiSync && (
              <React.Fragment>
                <button className="data-input-footer__button" onClick={openYardiSyncModal}>
                  {yardiSyncedFieldNames ? (
                    <React.Fragment>
                      <CheckCircle size={20} />
                      {t('checklists-create-task:dataInput.syncingWithYardi', {
                        fieldNames: yardiSyncedFieldNames,
                      })}
                    </React.Fragment>
                  ) : (
                    <React.Fragment>
                      <ArrowsClockwise size={20} />
                      {t('checklists-create-task:dataInput.syncWithYardiField')}
                    </React.Fragment>
                  )}
                </button>
                <div className="data-input-footer__divider" />
                {yardiSyncModalOpen && (
                  <YardiSyncModal
                    dataInputState={dataInputState}
                    selectedBuilding={selectedBuilding}
                    onClose={closeYardiSyncModal}
                    yardiCustomRecords={yardiCustomRecords}
                    setYardiCustomRecords={setYardiCustomRecords}
                    onSave={onYardiModalSave}
                  />
                )}
              </React.Fragment>
            )}
            <button className="data-input-footer__button" disabled={disabled} onClick={onCopy}>
              <Icon icon="copy" />
            </button>
            <button className="data-input-footer__button" disabled={disabled} onClick={onRemove}>
              <Icon icon="delete" />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

DataInput.propTypes = {
  onRemove: PropTypes.func,
  disabled: PropTypes.bool,
  onCopy: PropTypes.func,
  onFieldChange: PropTypes.func,
  onAddOption: PropTypes.func,
  onRemoveOption: PropTypes.func,
  onChangeOption: PropTypes.func,
  onShowSetFrom: PropTypes.func,
  onShowAddProvider: PropTypes.func,
  onRemoveProvider: PropTypes.func,
  onEditProvider: PropTypes.func,
  onProviderDragEnd: PropTypes.func,
  dataInputState: PropTypes.object,
  dataInputIndex: PropTypes.number,
  excludeForm: PropTypes.bool,
  selectedBuilding: PropTypes.object,
  yardiCustomRecords: PropTypes.object,
  setYardiCustomRecords: PropTypes.func,
  onYardiSyncFieldsChange: PropTypes.func,
  inputs: PropTypes.array,
}

export default DataInput
